
type Link = {
  type: string;
  key?: string;
}

type Links = {
  [key: string]: Link;
}

type Collections = {
  [collection: string]: {
    [id: number | string]: any;
  }
}

/*
  This method mutates an API response object to map linked records that were included in the results.

  `data` is an API response object that will look like:
  {
    categories: [
      { id: 15, ..., links: { forum: 1, ... } },
    ],
    forums: [
      { id: 1, ..., links: { ... } },
    ],
    sugggestions: [
      { id: 1234, ..., links: { forum: 1, category: 15, ... } },
    ],
    links: {
      'categories.forum': { type: 'forums' },
      'suggestions.forum': { type: 'forums' },
      'suggestions.category': { type: 'category' },
      'suggestions.merge_candidates': { type: 'suggestions', key: 'merge_candidates' },
      ...,
    },
    pagination: { ... },
  }

  After mapping is applied, linked records can be accessed as `suggestion.forum`, `suggestion.category`, etc.

  Terminology:
  'collection' - Grouping of response objects by their type (ie 'suggestions', 'forums', 'categories')
  'record' - A particular item inside a collection. All records will at least include an 'id' field.
*/

export function applyLinkedCollections(data: { [key: string]: any }) {
  if (!data) { return data; }

  const { links, ...values } = data;
  if (!links) { return data; }

  const collections = getCollections(values);
  const mappedAliases = aliasedLinks(links);

  for (const [collectionName, collection] of Object.entries(data)) {
    // Apparently we only support linked records inside array currently?
    if (!(collection && collection.length)) {
      continue;
    }
    applyLinksForCollection(collectionName, collection);
  }

  function applyLinksForCollection(collectionName: string, collection: any[]) {
    for (const record of collection) {
      if (!record.links) { continue; }
      applyLinksForRecord(collectionName, record);
    }
  }

  function applyLinksForRecord(collectionName: string, record: any) {
    for (const [linkKey, link] of Object.entries(record.links as { [key: string]: number | number[] | null })) {
      const collectionKey = mappedAliases[collectionName] || collectionName;
      const mapping = links[`${collectionKey}.${linkKey}`];
      if (!mapping) { continue; }

      const mappedCollection = collections[mapping.key || mapping.type];

      if (Array.isArray(link)) {
        // Don't set an empty array if the collection wasn't included at all
        if (mappedCollection) {
          record[linkKey] = [];
          link.forEach((id) => mappedCollection[id] && record[linkKey].push(mappedCollection[id]));
        }
      } else if (!link) {
        record[linkKey] = null;
      } else if (mappedCollection && mappedCollection[link]) {
        record[linkKey] = mappedCollection[link];
      }
    }
  }
}

function getCollections(data: { [key: string]: any }) {
  const collections: Collections = {};
  for (const [key, collection] of Object.entries(data)) {
    collections[key] = {};
    // Collections are always an array of objects with an id
    if (collection?.length) {
      collection.forEach((record: any) => {
        if (record.id) {
          collections[key][record.id] = record;
        }
      });
    }
  }
  return collections;
}

function aliasedLinks(links: Links) {
  const mappedAliases: { [type: string]: string } = {};

  for (const [_, value] of Object.entries(links)) {
    if (value.key) {
      mappedAliases[value.key] = value.type;
    }
  }
  return mappedAliases;
}
